import { horizontalStacked } from "../options/horizontal-stacked";
import { verticalStacked } from "../options/vertical-stacked";
import { formatNumberWithCommas } from "./format-commas";
import getEmphasis from "./get-emphasis";
import { lengthDataHandler } from "./get-length";
import { groupArrays } from "./group-arrays";
import { handleTooltipFormatting } from "./handle-formatting";
import { getTextStyle } from "./text-style";

type TProps = {
  primaryColor: string | undefined;
  colors: string[];
  type: string;
  data: Record<string, string>[][];
  payload: Record<string, unknown>;
};

export const stackedData = ({
  data,
  colors,
  primaryColor,
  type,
  payload,
}: TProps) => {
  const annotationTextStyle = getTextStyle(payload, "annotation");
  const [seriesTitles = [], ...restData] = data;
  seriesTitles.shift();
  const grouped = groupArrays(
    restData.map((row) => row.filter((cell) => !!cell?.value)),
  );
  let [axisTitles = [], ...gropedData] = grouped;
  const titles = axisTitles.map((cell) => cell?.value ?? "");

  if (type === "horizontal-stacked") {
    titles.reverse();
    gropedData = gropedData.map((arr) => arr.reverse());
  }

  const isInsideLike =
    payload.annotationPosition === "inside" ||
    payload.annotationPosition === "all";

  let series = gropedData.map((arr, i) => {
    const mapped = arr.map((cell) => cell?.value ?? 0);
    const seriesColor = colors[i] ?? primaryColor;
    return {
      data: mapped,
      type: "bar",
      stack: "total",
      itemStyle: { color: seriesColor },
      name: seriesTitles[i]?.value || "",
      // universalTransition: true,
      animationDurationUpdate: 1000,
      label: {
        ...seriesLabel({ payload, type, data: gropedData, index: i }),
        ...annotationTextStyle,
        color: !isInsideLike ? annotationTextStyle.color : undefined,
      },
      tooltip: (params: Record<string, string | number>) =>
        handleTooltipFormatting(params, payload.prefix, payload.suffix),
      emphasis: getEmphasis(seriesColor),
    };
  });

  if (payload.showAnnotation && payload.annotationPosition === "all") {
    const sums = (gropedData[0] ?? []).map((_, dataIndex) => {
      let sum = 0;
      gropedData.forEach((arr) => {
        const value = arr[dataIndex]?.value;
        if (typeof value === "number") sum += value;
      });
      return sum;
    });
    const outSlidePosition = type === "horizontal-stacked" ? "right" : "top";
    const prefixValue = (payload.prefix as string) ?? "";
    const suffixValue = (payload.suffix as string) ?? "";
    (series as unknown[]).push({
      data: sums.map(() => 0),
      type: "bar",
      stack: "total",
      itemStyle: { color: "transparent", borderColor: "transparent" },
      name: "",
      animationDurationUpdate: 1000,
      label: {
        ...annotationTextStyle,
        show: true,
        position: outSlidePosition,
        formatter: ({ dataIndex }: { dataIndex: number }) =>
          `${prefixValue}${formatNumberWithCommas(sums[dataIndex])}${suffixValue}`,
        color: annotationTextStyle.color,
      },
      silent: true,
      tooltip: { show: false },
    });
  }

  if (series.length === 1) {
    if (type === "horizontal-stacked") {
      colors = colors.splice(0, gropedData[0].length);
      if (colors.length < gropedData[0].length) {
        colors = colors.concat(
          Array(gropedData[0].length - colors.length).fill(primaryColor),
        );
      }
      colors.reverse();
    }
    const seriesData = series[0].data.map(
      (value: number | string, index: number) => {
        const itemColor = colors[index] || primaryColor;
        return {
          value: value,
          itemStyle: { color: itemColor },
          emphasis: getEmphasis(itemColor || ""),
        };
      },
    );
    series = [
      {
        data: seriesData,
        type: "bar",
        stack: "total",
        name: seriesTitles[0]?.value || "",
        // universalTransition: true,
        animationDurationUpdate: 1000,
      },
    ] as any[];
  }
  return {
    option:
      type === "vertical-stacked"
        ? verticalStacked(payload, { titles, series })
        : horizontalStacked(payload, { titles, series }),
    ...lengthDataHandler(titles, series),
  };
};

const seriesLabel = ({
  payload,
  type,
  data,
  index,
}: {
  payload: Record<string, unknown>;
  type: string;
  data: Record<string, string | number>[][];
  index: number;
}) => {
  const { showAnnotation, annotationPosition, color, prefix, suffix } = payload;
  const outSlidePosition = type === "horizontal-stacked" ? "right" : "top";
  const isSum = annotationPosition === "sum";
  const isAll = annotationPosition === "all";
  const isInsideLike = annotationPosition === "inside" || isAll;

  const showLabel =
    showAnnotation && ((isSum && data.length - 1 === index) || !isSum);

  const prefixValue = prefix ?? "";
  const suffixValue = suffix ?? "";

  const label: Record<string, unknown> = {
    show: showLabel,
    position: isInsideLike ? "inside" : outSlidePosition,
    formatter: ({ dataIndex }: { dataIndex: number }) => {
      if (!isSum)
        return `${prefixValue}${formatNumberWithCommas(data[index][dataIndex]?.value)}${suffixValue}`;
      let sum = 0;
      data.forEach((arr) => {
        const value = arr[dataIndex]?.value;
        if (typeof value === "number") sum += value;
      });
      return `${prefixValue}${formatNumberWithCommas(sum)}${suffixValue}`;
    },
    fontWeight: isInsideLike ? 700 : "normal",
  };
  if (!isInsideLike) label["color"] = color;
  return label;
};
